import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import TimeAndDate from '../TimeAndDate';
import BusinessDetailsRow from '../BusinessDetailsRow';
import addressIcon from '../../../../assets/svg/location-icon.svg';
import websiteIcon from '../../../../assets/svg/website-icon.svg';
import phoneNoIcon from '../../../../assets/svg/phone-icon.svg';
import TransactionNameAndAmount from '../TransactionNameAndAmount';
import BusinessPictureMap from '../BusinessPictureMap';
import TransactionReceipt from '../TransactionReceipt';
import TextArea from '../Textarea';
import {
  ENRICHMENT_TYPES, TRANSACTION_DETAILS_DATA, TRANSACTION_DETAILS_TEXTS, TRANSACTION_TYPES, VERIFY,
} from '../../constants';
import CheckPhoto from '../CheckPhoto';
import { useStore } from '../../../../store';
import TwoDivsInOne from '../../../../generic-components/TwoDivsInOne';
import { getLocalAmount } from '../../../../helpers/currency.helper';
import { getTransactionStatus, getNameFromTransaction } from '../../utils';
import { TransactionInitiator } from '../TransactionInitiator';
import Heading from '../../../../generic-components/Heading';
import { refactoredTimeAndDate } from '../../../../helpers/timeFormatter.helper';
import { DATE_FORMAT } from '../../../../constants/general';
import { CheckDetails } from '../CheckDetails/CheckDetails';
import { ChooseCategory } from '../../../categorizeTransacrions/components/ChooseCategory';
import { CategorizationPopup } from '../../../dashboard/components/CategorizationPopup';
import { CategorizationStrip } from '../../../categorizeTransacrions/components/CategorizationStrip';
import { ACTION_TYPES, CATEGORIZATION_TYPES } from '../../../categorizeTransacrions/constants';
import { ITransaction } from '../../../../types';
import { useCategorizeTransactionMutation } from '../../../../hooks/mutations/useCategorizeTransactionMutation';
import {
  areAllRequiredDataReady,
  getBusinessPercentByActionType,
  sendCategorizationEventToMixpanel,
} from '../../utils/categorization';

export const CategorizationModes = {
  REGULAR: 'REGULAR',
  TRIGGER: 'TRIGGER',
};

interface IProps {
  handleOnSuccessUpload?: (value:string, property: string)=>void;
  detailsComponentsMap: any;
  detailsDataObject: any;
  categorizationMode?: string
  isReadOnly?: boolean;
}

const TransactionDetails = ({
  handleOnSuccessUpload,
  detailsComponentsMap,
  detailsDataObject,
  categorizationMode,
  isReadOnly,
}: IProps) => {
  const {
    CHECK_NUMBER, ROUTING_NUMBER, ACCOUNT_NUMBER, ARRIVAL_DATE,
  } = TRANSACTION_DETAILS_TEXTS;
  const { TITLE } = VERIFY;
  const {
    TEXT_AREA, CHAR_COUNTER, SAVE_BTN,
  } = TRANSACTION_DETAILS_DATA;
  const categoriesList = useStore.getState().getCategoriesList();
  const customerData = useStore(state => state.user);
  const setTriggeredActionType = useStore(state => state.setTriggeredActionType);
  const { customerId } = customerData;
  const [showCategorizationPopup, setShowCategorizationPopup] = useState<boolean>(false);
  const [actionType, setActionType] = useState<string>('');
  const { mutateAsync: mutateCategorizeTransaction } = useCategorizeTransactionMutation();
  const showCategorizationUI = categorizationMode === CategorizationModes.TRIGGER || categorizationMode === CategorizationModes.REGULAR;
  const showChooseCategory = showCategorizationUI && !!categoriesList?.categories && !!detailsDataObject;
  const { t } = useTranslation();
  const showRejectionReason = () => getTransactionStatus(detailsDataObject, t) === TRANSACTION_TYPES.CHECK_STATUS.REJECTED;
  const shouldDisplayMap = (category: string) => {
    if (!category) return false;
    return ENRICHMENT_TYPES.NON_LOCATION_ENRICH_TYPES.includes(category);
  };
  const updateCategorizationToServer = async () => {
    try {
      await mutateCategorizeTransaction({
        customerId,
        transactionId: detailsDataObject.transactionId,
        categoryDetails: {
          categoryId: actionType === ACTION_TYPES.LIFE ? null : detailsDataObject?.categoryDetails?.categoryExternalId || detailsDataObject?.suggestedTaxCategoryId,
          businessPercent: getBusinessPercentByActionType(actionType, detailsDataObject),
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  const handleCategorySelection = async (catId: string | null, businessPercent?: number) => {
    if (catId !== null) {
      const localClone = { ...detailsDataObject };
      localClone.categoryDetails.categoryExternalId = catId;
      localClone.categoryDetails.businessPercent = businessPercent || businessPercent === 0 ? businessPercent : getBusinessPercentByActionType(actionType, detailsDataObject);
      detailsDataObject = localClone;
      if ([ACTION_TYPES.WORK, ACTION_TYPES.LIFE, ACTION_TYPES.SPLIT].includes(actionType) || (businessPercent || businessPercent === 0) || (actionType === ACTION_TYPES.CHOOSE_CATEGORY && !businessPercent && localClone?.categorizationType === CATEGORIZATION_TYPES.INCOME)) {
        await updateCategorizationToServer();
        sendCategorizationEventToMixpanel(detailsDataObject, categoriesList);
      }
    }
    setActionType('');
    setShowCategorizationPopup(false);
  };
  const canFinalizeCategorization = (transaction: ITransaction, iActionType: string) => (
    ([ACTION_TYPES.WORK, ACTION_TYPES.LIFE].includes(iActionType) && areAllRequiredDataReady(transaction, iActionType))
  );

  const handleCategorizationAction = async (iActionType: string) => {
    if (categorizationMode === CategorizationModes.TRIGGER) {
      setTriggeredActionType(iActionType);
    } else {
      setActionType(iActionType);
      if (canFinalizeCategorization(detailsDataObject, iActionType)) {
        const catId = iActionType === ACTION_TYPES.LIFE ? null : detailsDataObject?.categoryDetails?.categoryExternalId || detailsDataObject?.suggestedTaxCategoryId;
        const businessPercent = getBusinessPercentByActionType(iActionType, detailsDataObject);
        try {
          await mutateCategorizeTransaction({
            customerId,
            transactionId: detailsDataObject.transactionId,
            categoryDetails: {
              categoryId: catId,
              businessPercent,
            },
          });
          if ([ACTION_TYPES.WORK, ACTION_TYPES.LIFE].includes(iActionType)) {
            detailsDataObject.categoryDetails.businessPercent = businessPercent;
          }
          sendCategorizationEventToMixpanel(detailsDataObject, categoriesList);
        } catch (e) {
          console.error(e);
        }
      } else {
        setShowCategorizationPopup(true);
      }
    }
  };

  const handleChooseCategoryClick = () => {
    if (categorizationMode === CategorizationModes.TRIGGER) {
      setTriggeredActionType(ACTION_TYPES.CHOOSE_CATEGORY);
    } else {
      setActionType(ACTION_TYPES.CHOOSE_CATEGORY);
      setShowCategorizationPopup(true);
    }
  };
  const getSourceAccountDetails = () => {
    if (detailsDataObject?.amount > 0 && detailsDataObject?.sourceAchNumber && detailsDataObject?.sourceRoutingNumber) {
      return {
        accountNumber: detailsDataObject.sourceAchNumber,
        routingNumber: detailsDataObject.sourceRoutingNumber,
      };
    }
    return null;
  };

  const getTargetAccountDetails = () => {
    if (detailsDataObject?.amount < 0 && detailsDataObject?.targetAchNumber && detailsDataObject?.targetRoutingNumber) {
      return {
        accountNumber: detailsDataObject.targetAchNumber,
        routingNumber: detailsDataObject.targetRoutingNumber,
      };
    }
    return null;
  };
  const accountDetails = getSourceAccountDetails() || getTargetAccountDetails();
  const renderCategorizationPopup = () => (
    showCategorizationPopup
    && detailsDataObject
    && (
      <CategorizationPopup
        categoryList={categoriesList?.categories}
        handleCategoryPick={handleCategorySelection}
        transaction={detailsDataObject}
        actionType={actionType ?? ''}
      />
    )
  );

  return !detailsDataObject?.transactionId ? <div className='hidden' /> : (
    <>
      <TimeAndDate timeDate={detailsDataObject.timestamp} />
      <div className='h-0 border-b border-bw04 my-4' />
      <TransactionNameAndAmount
        isRejected={showRejectionReason()}
        name={getNameFromTransaction(detailsDataObject)}
        transactionAmount={detailsDataObject.amount.toString()}
        iconSrc={detailsComponentsMap?.HeaderIcon || undefined}
      />
      {((detailsDataObject.onHold || detailsDataObject.transactionStatus === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.HOLD)
          && detailsDataObject.trxType === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.ACH
          && (detailsDataObject.externalTransactionType === TRANSACTION_TYPES.DIRECT_DEPOSIT || detailsDataObject.trxTypeParams === TRANSACTION_TYPES.DIRECT_DEPOSIT))
        ? (
          <>
            <div className='h-0 border-b border-bw05 my-4' />
            <TwoDivsInOne firstDivClassName='font-metricMedium text-base text-bw08 mt-6 mb-2' firstDivValue={t(TITLE)} secondDivClassName='text-xs text-bw06' secondDivValue={detailsDataObject.holdText} />
            {detailsDataObject.holdReason === TRANSACTION_TYPES.HOLD_REASON.NAME_MISMATCH
                && (
                <>
                  <div className='h-0 border-b border-bw05 my-4' />
                  <span className='font-metric text-base text-bw07'>{t(VERIFY.ON_MOBILE)}</span>
                </>
                )}
          </>
        )
        : (
          <>
            {
              showChooseCategory && (
                <div className='mt-[7px] mb-[10px]'>
                  <ChooseCategory isReadOnly={isReadOnly} transactionsCategories={categoriesList?.categories} transaction={detailsDataObject} onCategorySelection={handleChooseCategoryClick} />
                </div>
              )
            }
            {showCategorizationUI && (
              <CategorizationStrip
                isReadOnly={isReadOnly}
                transaction={detailsDataObject}
                handleCategorizationActionClick={handleCategorizationAction}
              />
            )}
            {showCategorizationUI && renderCategorizationPopup()}
            {accountDetails?.accountNumber && accountDetails?.routingNumber && <div className='h-0 border-b border-bw05 my-6' />}
            {accountDetails?.accountNumber && accountDetails?.routingNumber && <TwoDivsInOne containerClassName='text-base font-metric flex justify-between mb-2' firstDivClassName='text-bw07' firstDivValue={t(ACCOUNT_NUMBER)} secondDivClassName='text-bw08' secondDivValue={accountDetails.accountNumber} />}
            {accountDetails?.accountNumber && accountDetails?.routingNumber && <TwoDivsInOne containerClassName='text-base font-metric flex justify-between mb-2' firstDivClassName='text-bw07' firstDivValue={t(ROUTING_NUMBER)} secondDivClassName='text-bw08' secondDivValue={accountDetails.routingNumber} />}
            {detailsDataObject?.transactionStatus === TRANSACTION_TYPES.TRANSACTION_DETAILS_STATUS.PENDING && detailsDataObject?.expectedClearDate && (
            <TwoDivsInOne secondDivId='estimatClearDateValue' containerClassName='text-base font-metric flex justify-between mb-2' firstDivClassName='text-bw07' firstDivValue={t(ARRIVAL_DATE)} secondDivClassName='text-bw08 text-right' secondDivValue={format(new Date(detailsDataObject?.expectedClearDate || ''), 'MMMM dd, yyyy')} />
            )}
            {detailsDataObject.checkDetailsData?.checkNumber && detailsDataObject.checkDetailsData?.checkTransactionId && <div className='w-full h-0 border-b border-bw05 my-4' />}
            {detailsComponentsMap?.CheckPhoto && detailsDataObject.checkDetailsData?.checkTransactionId && <CheckPhoto className='flex justify-between' transactionId={detailsDataObject.checkDetailsData.checkTransactionId} />}
            {detailsComponentsMap?.CheckNumber && detailsDataObject.checkDetailsData?.checkNumber && <TwoDivsInOne containerClassName='text-base font-metric flex justify-between' firstDivClassName='text-bw07' firstDivValue={t(CHECK_NUMBER)} secondDivClassName='text-bw08' secondDivValue={detailsDataObject.checkDetailsData.checkNumber} />}
            {detailsComponentsMap?.EstimatedArrivalDate && detailsDataObject.checkDetailsData?.estimatedArrivalDate && (
            <TwoDivsInOne secondDivId='estimatArrivalDateValue' containerClassName='text-base font-metric flex justify-between mb-2' firstDivClassName='text-bw07' firstDivValue={t(ARRIVAL_DATE)} secondDivClassName='text-bw08 text-right' secondDivValue={format(new Date(detailsDataObject.checkDetailsData.estimatedArrivalDate || ''), 'MMMM dd, yyyy')} />
            )}
            {detailsDataObject.currencyCode !== detailsDataObject.localCurrencyCode && detailsDataObject.localAmount && <div className='font-metric text-bw07 text-13px pt-[1rem]'>{`Original Currency ${getLocalAmount(detailsDataObject.localAmount, detailsDataObject.localCurrencyCode)}`}</div>}
            {detailsDataObject.allowComment || detailsDataObject.comment ? <div className='w-full h-0 border-b border-bw05 my-4' /> : ''}
            {detailsDataObject?.locationDetails?.address && shouldDisplayMap(detailsDataObject?.externalTransactionType) ? <BusinessPictureMap locationDetails={detailsDataObject.locationDetails} /> : ''}
            {detailsDataObject.locationDetails?.address && detailsComponentsMap?.BusinessDetailsRow?.address && <BusinessDetailsRow title={detailsDataObject.locationDetails.address} iconSrc={addressIcon} />}
            {detailsDataObject.locationDetails?.website && detailsComponentsMap?.BusinessDetailsRow?.website && <BusinessDetailsRow title={detailsDataObject.locationDetails.website} iconSrc={websiteIcon} className='text-action03 hover:bg-action04' />}
            {detailsDataObject.locationDetails?.phoneNumber && detailsComponentsMap?.BusinessDetailsRow?.phoneNo && <BusinessDetailsRow title={detailsDataObject.locationDetails.phoneNumber} iconSrc={phoneNoIcon} />}
            {showRejectionReason() && (
            <div className='flex flex-col'>
              <span className='font-metric text-base text-bw07 h-[24px]'>{t('TRANSACTION_DETAILS_REJECTION_REASON')}</span>
              <span className='text-base text-bw08 font-metricMedium'>{detailsDataObject?.checkDetailsData?.operatorMessage ?? ''}</span>
            </div>
            )}
            {detailsComponentsMap?.TransactionEffectiveDate && detailsDataObject?.effectiveDate && (
            <div className='flex justify-between w-full font-metricMedium text-base relative pb-[1rem] border-b border-b-bw05'>
              <Heading id='trxEffectiveDate' title={t('TRANSACTION_EFFECTIVE_DATE')} className='font-metricR text-[14px] text-bw07' />
              <Heading id='trxEffectiveDateValue' title={refactoredTimeAndDate(detailsDataObject.effectiveDate, 'details', DATE_FORMAT.NUMBERS)} className='font-metricR text-[16px] text-bw08' />
            </div>
            )}
            {detailsComponentsMap?.mailCheckDetails && (<CheckDetails checkData={detailsDataObject?.mailCheckDetailsData} />)}
            {detailsDataObject.allowComment && (
              <TextArea
                handleOnSuccessUpload={handleOnSuccessUpload}
                canAddNote={!isReadOnly && detailsDataObject.allowComment}
                transactionId={detailsDataObject.transactionId}
                saveButtonClassName={SAVE_BTN.CLASS_NAME}
                disabled={isReadOnly || !detailsDataObject.allowComment}
                counterClassName={CHAR_COUNTER.CLASS_NAME}
                textAreaClassName={TEXT_AREA.CLASS_NAME}
                characterCapacity={TEXT_AREA.CHAR_CAPACITY}
                containerClassName={TEXT_AREA.CONTAINER.CLASS_NAME}
                textAreaValue={detailsDataObject.comment ?? ''}
                textAreaPlaceholder={t(TEXT_AREA.PLACE_HOLDER)}
              />
            )}
            {detailsDataObject.allowCategorizationEnum === 'READ_WRITE' && <TransactionReceipt handleOnSuccessUploadReceipt={handleOnSuccessUpload} transactionId={detailsDataObject.transactionId} receiptData={detailsDataObject.categoryDetails?.receiptPath} canAddReceipt={!isReadOnly && detailsDataObject.allowCategorizationEnum === 'READ_WRITE'} />}
            {detailsDataObject.initiatorFirstName && <TransactionInitiator detailsDataObject={detailsDataObject} />}
          </>
        )}

    </>
  );
};
export default TransactionDetails;
